import { useEffect, useState } from 'react';
import { isEmailAddress } from '@acs/shared';
import { Card, Field, Input, SaveBar, Switch, useToast } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useAuth } from '../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import { friendlyError } from '../../shared/api/errors.js';
import { useSaveSettings } from './hooks.js';

/**
 * Owner-facing email: "tell me when a booking lands" (doc 29), and — a second
 * switch — "tell me when a customer moves or cancels one" (doc 87). Changes the
 * owner makes themselves never email them, so the switches only ever describe
 * what someone else did.
 *
 * Its own card rather than a third block inside {@link NotificationsCard},
 * because the two answer different questions. That one is what a business's
 * CLIENTS receive and is priced per message; this is where the OWNER wants to be
 * reached about their own business, and it is free. Putting them in one card
 * invites the reading that switching one on switches on the other's costs.
 *
 * Off by default — nobody's inbox starts filling up because we deployed.
 *
 * The address box is pre-filled with the account's sign-in email, which is the
 * only address we know, and it is a DEFAULT rather than a fixed value: plenty of
 * owners sign in with a personal address and want bookings going to the shop's
 * inbox (or to a manager). What is not stored is the difference between "empty"
 * and "the same as my sign-in" — leaving the prefill untouched saves it
 * explicitly, so a later change of sign-in address cannot silently redirect
 * where their bookings are announced.
 */
export function OwnerEmailCard() {
  const { t } = useI18n();
  const business = useActiveBusiness();
  const { user } = useAuth();
  const save = useSaveSettings(business.id);
  const { toast } = useToast();

  const stored = business.notifications.email;
  const fallback = user?.email ?? '';

  const [onBooking, setOnBooking] = useState(stored.onBooking);
  const [onChange, setOnChange] = useState(stored.onChange);
  const [address, setAddress] = useState(stored.address || fallback);

  // Another tab must win over a stale form. The prefill is re-applied only while
  // nothing is stored, so it can never overwrite an address an owner typed.
  useEffect(() => {
    setOnBooking(stored.onBooking);
    setOnChange(stored.onChange);
    setAddress(stored.address || fallback);
  }, [stored.onBooking, stored.onChange, stored.address, fallback]);

  const trimmed = address.trim();
  const anyOn = onBooking || onChange;
  // Only an error once they have switched it on: an address typed into a
  // switched-off field is not yet a promise we have to keep, and marking it red
  // while they are still deciding is nagging.
  const invalid = anyOn && trimmed.length > 0 && !isEmailAddress(trimmed);
  const dirty = onBooking !== stored.onBooking || onChange !== stored.onChange || trimmed !== stored.address;
  // Switching it on with nothing to send to would save a setting that can only
  // produce suppressed rows, so the switches and the box are saved as one thing.
  const blocked = invalid || (anyOn && trimmed.length === 0);

  const discard = () => {
    setOnBooking(stored.onBooking);
    setOnChange(stored.onChange);
    setAddress(stored.address || fallback);
  };

  return (
    <Card title={t('ownerEmail.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('ownerEmail.hint')}</p>

      <Field label={t('ownerEmail.enabled')}>
        <Switch
          checked={onBooking}
          onChange={(e) => setOnBooking(e.target.checked)}
          label={t('ownerEmail.enabledLabel')}
        />
      </Field>

      <div style={{ marginTop: 16 }}>
        <Field label={t('ownerEmail.changes')}>
          <Switch
            checked={onChange}
            onChange={(e) => setOnChange(e.target.checked)}
            label={t('ownerEmail.changesLabel')}
          />
        </Field>
      </div>

      <div style={{ marginTop: 16, maxWidth: 380 }}>
        <Field
          label={t('ownerEmail.address')}
          hint={t('ownerEmail.addressHint')}
          error={invalid ? t('ownerEmail.addressInvalid') : undefined}
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            spellCheck={false}
            value={address}
            placeholder={fallback || t('ownerEmail.placeholder')}
            onChange={(e) => setAddress(e.target.value)}
          />
        </Field>
      </div>

      {dirty && (
        <SaveBar
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          // The bar says what is missing instead of offering a button that would
          // save a setting able only to produce suppressed rows. `dirty={false}`
          // is what disables Save; the way out is the field above, or switching
          // the toggle back off.
          message={blocked ? t('ownerEmail.addressRequired') : t('ui.unsavedChanges')}
          dirty={!blocked}
          saving={save.isPending}
          onDiscard={discard}
          onSave={() => {
            if (blocked) return;
            save.mutate(
              { email: { onBooking, onChange, address: trimmed } },
              {
                onSuccess: () => toast({ title: t('ownerEmail.saved'), tone: 'success' }),
                onError: (e) => toast({ title: friendlyError(t, e), tone: 'danger' }),
              },
            );
          }}
        />
      )}
    </Card>
  );
}
